/**
 * Definição dos papéis de usuário disponíveis no sistema
 * Estes papéis são usados para controle de acesso baseado em função
 */
export const USER_ROLES = Object.freeze({
  COMPANY_OWNER: 'COMPANY_OWNER',   // Proprietário da empresa
  COMPANY_ADMIN: 'COMPANY_ADMIN',   // Administrador da empresa
  COMPANY_STAFF: 'COMPANY_STAFF',   // Funcionário da empresa
  COMPANY_SECRETARY: 'COMPANY_SECRETARY', // Secretário da empresa
});

/**
 * Mapeamento de papéis para níveis hierárquicos
 * Quanto menor o número, maior o nível de acesso
 */
export const ROLE_HIERARCHY = Object.freeze({
  [USER_ROLES.COMPANY_OWNER]: 1,
  [USER_ROLES.COMPANY_ADMIN]: 2,
  [USER_ROLES.COMPANY_STAFF]: 3,
  [USER_ROLES.COMPANY_SECRETARY]: 4,
});

/**
 * Níveis cumulativos de acesso: espelho de public/api/auth/RoleAccessMap.php.
 * Equipe (STAFF e SECRETARY) < Administrador(a) < Proprietário(a). A tela usa os
 * níveis para evitar a chamada e explicar; quem bloqueia é a API (403 ROLE_DENIED).
 */
export const ROLE_LEVELS = Object.freeze({ TEAM: 1, ADMIN: 2, OWNER: 3 });

export const roleLevel = (roleName) => {
  switch (roleName) {
    case USER_ROLES.COMPANY_OWNER: return ROLE_LEVELS.OWNER;
    case USER_ROLES.COMPANY_ADMIN: return ROLE_LEVELS.ADMIN;
    case USER_ROLES.COMPANY_STAFF:
    case USER_ROLES.COMPANY_SECRETARY: return ROLE_LEVELS.TEAM;
    default: return 0;
  }
};

/**
 * Áreas da tela e o nível mínimo de cada uma (mesma régua do RoleAccessMap).
 * Tudo que não está aqui é da Equipe: agenda, pacientes, WhatsApp, leads, documentos.
 */
export const ACCESS_AREAS = Object.freeze({
  financial: ROLE_LEVELS.ADMIN,
  reports: ROLE_LEVELS.ADMIN,
  statistics: ROLE_LEVELS.ADMIN,
  settings: ROLE_LEVELS.ADMIN,          // configurações da clínica (escrita)
  team: ROLE_LEVELS.ADMIN,              // convidar, editar e remover pessoas
  subscription: ROLE_LEVELS.ADMIN,      // trocar plano, checkout, portal de pagamento
  firstSteps: ROLE_LEVELS.ADMIN,        // card Primeiros passos e guia na tela
  subscriptionCancel: ROLE_LEVELS.ADMIN, // cancelar o plano ou remover módulos
  dataExport: ROLE_LEVELS.OWNER,        // Backup e dados
  patientLegal: ROLE_LEVELS.OWNER,      // exportar, anonimizar ou excluir um paciente
  audit: ROLE_LEVELS.OWNER,
  closure: ROLE_LEVELS.OWNER,
  transfer: ROLE_LEVELS.OWNER,
  terms: ROLE_LEVELS.OWNER,
});

/** O papel alcança a área? Área desconhecida nunca passa (mesma regra do backend). */
export const canAccess = (roleName, area) => {
  const min = ACCESS_AREAS[area];
  return !!min && roleLevel(roleName) >= min;
};

/** Ids de system_user_roles (iguais no seed e em produção), para convidar e editar. */
export const ROLE_IDS = Object.freeze({
  [USER_ROLES.COMPANY_OWNER]: 1,
  [USER_ROLES.COMPANY_ADMIN]: 2,
  [USER_ROLES.COMPANY_STAFF]: 3,
  [USER_ROLES.COMPANY_SECRETARY]: 4,
});

/**
 * Nomes de exibição de reserva. O nome oficial vem de system_user_roles.displayName
 * (getRoleDisplayName); estes só valem se a configuração ainda não carregou.
 */
export const ROLE_DISPLAY_NAMES = Object.freeze({
  [USER_ROLES.COMPANY_OWNER]: 'Proprietário(a)',
  [USER_ROLES.COMPANY_ADMIN]: 'Administrador(a)',
  [USER_ROLES.COMPANY_STAFF]: 'Equipe',
  [USER_ROLES.COMPANY_SECRETARY]: 'Equipe (antigo Recepção)',
});

/** Nome da marcação independente do papel, que só o(a) Proprietário(a) liga. */
export const HEALTH_PROFESSIONAL_LABEL = 'Profissional de saúde';

/**
 * Papéis que podem ser escolhidos ao convidar ou editar alguém, na ordem de exibição.
 * O proprietário não entra: é quem criou a conta e nunca é atribuído (UserRoles::canAssign).
 * A antiga Recepção (SECRETARY) virou Equipe na migration_v2 e não é mais atribuída.
 */
export const ASSIGNABLE_ROLES = Object.freeze([
  USER_ROLES.COMPANY_ADMIN,
  USER_ROLES.COMPANY_STAFF,
]);

/**
 * Resumo de cada papel atribuível para a tela de usuários (cards e escolha do papel).
 * Reflete as checagens reais (RoleGuard, hasRole, canEditUser e o backend).
 */
const TEAM_GUIDE = Object.freeze({
  tagline: 'Cuida da agenda e dos pacientes no dia a dia.',
  can: [
    'Agenda, cadastro de pacientes e lista de espera',
    'WhatsApp, leads e documentos',
    'Prontuário e atendimento, se tiver a marcação Profissional de saúde',
  ],
  cannot: [
    'Financeiro, relatórios e estatísticas',
    'Configurações, assinatura e equipe',
  ],
});

export const ROLE_GUIDE = Object.freeze({
  [USER_ROLES.COMPANY_ADMIN]: {
    tagline: 'Ajuda a cuidar do negócio: agenda, equipe e financeiro.',
    can: [
      'Tudo que a Equipe faz',
      'Financeiro, relatórios e estatísticas',
      'Configurações, assinatura e equipe',
      'Atendimentos, se marcado como quem atende',
    ],
    cannot: [
      'Backup e dados, auditoria e exclusão de pacientes',
    ],
  },
  [USER_ROLES.COMPANY_STAFF]: TEAM_GUIDE,
  // Antigo papel Recepção: mesma coisa que Equipe, só aparece em dados antigos
  [USER_ROLES.COMPANY_SECRETARY]: TEAM_GUIDE,
});

/**
 * Papéis que quem chama pode atribuir (espelho de UserRoles::canAssign): o
 * proprietário atribui todos; o administrador só a Equipe; a Equipe, nada.
 */
export const assignableRolesFor = (callerRole) => {
  if (callerRole === USER_ROLES.COMPANY_OWNER) return ASSIGNABLE_ROLES;
  if (callerRole === USER_ROLES.COMPANY_ADMIN) {
    return ASSIGNABLE_ROLES.filter((role) => role !== USER_ROLES.COMPANY_ADMIN);
  }
  return [];
};

/** O que só o proprietário da conta pode fazer (checagens COMPANY_OWNER no backend). */
export const OWNER_EXCLUSIVE = Object.freeze([
  'Marcar quem é profissional de saúde',
  'Cancelar a assinatura',
  'Baixar o backup dos dados',
  'Exportar, anonimizar ou excluir os dados de uma pessoa atendida',
  'Ver a auditoria de acessos',
  'Encerrar a conta',
]);
